import React, { useState, useEffect } from 'react';
import { subscribeToCollection, saveDocument, deleteDocument } from '../firebase';
import { getUserName } from '../utils/userUtils';

export default function PackingList({ users = [] }) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newItemName, setNewItemName] = useState("");
  const [newIsShared, setNewIsShared] = useState(true);
  const [carrierNameInput, setCarrierNameInput] = useState("");
  const [claimingItemId, setClaimingItemId] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    // Subscribe to packing updates
    const unsubscribe = subscribeToCollection("packing", (data) => {
      // Sort: Active items first, then packed.
      const sorted = [...data].sort((a, b) => {
        if (a.packed !== b.packed) return a.packed ? 1 : -1;
        return 0;
      });
      setItems(sorted);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItemName.trim()) return;

    const addedBy = localStorage.getItem("prine_user_name") || "Guest";
    const newItem = {
      id: `pack_${Date.now()}`,
      name: newItemName.trim(),
      isShared: newIsShared,
      addedBy,
      claimedBy: "",
      packed: false,
      packedBy: []
    };

    await saveDocument("packing", newItem.id, newItem);
    setNewItemName("");
  };

  const handleTogglePacked = async (item) => {
    const userName = localStorage.getItem("prine_user_name") || "";

    if (!item.isShared) {
      if (!userName.trim()) {
        alert("Please enter your name or log in to check off items!");
        return;
      }

      const currentPackedBy = Array.isArray(item.packedBy) ? item.packedBy : [];
      let newPackedBy;
      if (currentPackedBy.includes(userName)) {
        newPackedBy = currentPackedBy.filter(u => u !== userName);
      } else {
        newPackedBy = [...currentPackedBy, userName];
      }

      const updated = {
        ...item,
        packedBy: newPackedBy
      };
      await saveDocument("packing", item.id, updated);
    } else {
      const updated = {
        ...item,
        packed: !item.packed
      };
      await saveDocument("packing", item.id, updated);
    }
  };

  const handleStartClaim = (itemId) => {
    setClaimingItemId(itemId);
    const savedName = localStorage.getItem("prine_user_name") || "";
    setCarrierNameInput(savedName);
  };

  const handleConfirmClaim = async (item) => {
    if (!carrierNameInput) return alert("Please select your name to claim.");

    const updated = {
      ...item,
      claimedBy: carrierNameInput
    };

    await saveDocument("packing", item.id, updated);
    setClaimingItemId(null);
  };

  const handleUnclaim = async (item) => {
    if (!window.confirm(`Are you sure you want to remove ${getUserName(users, item.claimedBy)} as the packer?`)) return;
    const updated = {
      ...item,
      claimedBy: ""
    };
    await saveDocument("packing", item.id, updated);
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) return;
    await deleteDocument("packing", item.id);
  };

  const userName = localStorage.getItem("prine_user_name") || "";

  const isItemPacked = (item) => {
    if (item.isShared) {
      return !!item.packed;
    }
    return userName ? (Array.isArray(item.packedBy) && item.packedBy.includes(userName)) : false;
  };

  const activeItems = items.filter(item => !isItemPacked(item));
  const packedItems = items.filter(item => isItemPacked(item));

  const sharedActiveItems = activeItems.filter(item => item.isShared);
  const personalActiveItems = activeItems.filter(item => !item.isShared);

  return (
    <div className="tab-panel">
      <div className="view-title-container">
        <div className="view-title">
          <h2>🎒 Packing List</h2>
        </div>
      </div>

      <div className="packing-layout">
        {/* Left Column: Suggest Item Form */}
        <div className="glass-panel" style={{ padding: '24px' }}>
          <h3 style={{ fontFamily: 'var(--font-title)', fontSize: '1.2rem', marginBottom: '16px', color: 'var(--color-text-main)' }}>Suggest Item to Bring</h3>
          <form onSubmit={handleAddItem} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div className="form-group">
              <label>Item Description</label>
              <input
                type="text"
                placeholder="e.g. Sunscreen"
                className="input-field"
                value={newItemName}
                onChange={e => setNewItemName(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>Item Type</label>
              <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', cursor: 'pointer', color: 'var(--color-text-main)' }}>
                  <input
                    type="radio"
                    name="isShared"
                    checked={newIsShared === true}
                    onChange={() => setNewIsShared(true)}
                    style={{ cursor: 'pointer' }}
                  />
                  One person brings
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', cursor: 'pointer', color: 'var(--color-text-main)' }}>
                  <input
                    type="radio"
                    name="isShared"
                    checked={newIsShared === false}
                    onChange={() => setNewIsShared(false)}
                    style={{ cursor: 'pointer' }}
                  />
                  Everybody brings
                </label>
              </div>
              <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginTop: '4px', display: 'block', lineHeight: '1.3' }}>
                {newIsShared
                  ? "One person brings: These are shared items where only one person needs to bring it for the whole house (e.g. bluetooth speaker)."
                  : "Everybody brings: These are personal items that every person/family unit needs to bring for themselves (e.g. hiking gear)."
                }
              </span>
            </div>

            <button type="submit" className="btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: '6px' }}>
              Add to Packing List
            </button>
          </form>
        </div>

        {/* Right Column: Dynamic Checklist Grid */}
        <div className="packing-list-container">
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-chip">
                <span className="spinner"></span>
                <span>Syncing Packing Board...</span>
              </div>
            </div>
          ) : activeItems.length === 0 && packedItems.length === 0 ? (
            <div className="glass-panel" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              Packing list is completely empty. Suggest items we should bring above!
            </div>
          ) : (
            <>
              {/* One person brings Section */}
              <div className="packing-category-section" style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontFamily: 'var(--font-title)',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: 'var(--color-primary)',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid var(--color-glass-border)',
                  paddingBottom: '4px'
                }}>
                  One person brings ({sharedActiveItems.length})
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {sharedActiveItems.length === 0 ? (
                    <div style={{
                      fontSize: '0.8rem',
                      color: 'var(--color-text-muted)',
                      padding: '14px 20px',
                      background: 'rgba(255, 255, 255, 0.01)',
                      border: '1px dashed var(--color-glass-border)',
                      borderRadius: '12px',
                      fontStyle: 'italic',
                      textAlign: 'center'
                    }}>
                      No items to bring.
                    </div>
                  ) : (
                    sharedActiveItems.map(item => (
                      <div
                        key={item.id}
                        className={`packing-item-card ${item.claimedBy ? 'claimed' : ''} ${isItemPacked(item) ? 'packed' : ''}`}
                        style={isItemPacked(item) ? { opacity: 0.6 } : undefined}
                      >
                        <div className="packing-item-left">
                          <div
                            className={`packing-checkbox ${isItemPacked(item) ? 'checked' : ''}`}
                            onClick={() => handleTogglePacked(item)}
                          />

                          <div className="packing-text-block">
                            <span className="packing-name" style={isItemPacked(item) ? { textDecoration: 'line-through' } : undefined}>
                              {item.name}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px', flexWrap: 'wrap' }}>
                              <span className="packing-metadata">
                                Added by: <span>{getUserName(users, item.addedBy)}</span>
                              </span>
                              {isItemPacked(item) && item.claimedBy && (
                                <span style={{
                                  fontSize: '0.7rem',
                                  color: 'var(--color-accent, #10b981)',
                                  background: 'rgba(16, 185, 129, 0.08)',
                                  padding: '2px 8px',
                                  borderRadius: '10px',
                                  fontWeight: '600'
                                }}>
                                  ✓ Packed by: {getUserName(users, item.claimedBy)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="packing-item-actions">
                          {claimingItemId === item.id ? (
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <select
                                className="input-field"
                                value={carrierNameInput}
                                onChange={e => setCarrierNameInput(e.target.value)}
                                style={{ padding: '4px 10px', fontSize: '0.8rem', width: '130px' }}
                              >
                                <option value="" style={{ background: 'var(--color-bg-dropdown, #1e293b)', color: '#94a3b8' }}>-- Select --</option>
                                {users.map(u => (
                                  <option key={u.id} value={u.id} style={{ background: 'var(--color-bg-dropdown, #1e293b)', color: 'var(--color-text-main)' }}>
                                    {u.name}
                                  </option>
                                ))}
                              </select>
                              <button
                                className="btn-claim"
                                style={{ padding: '4px 10px' }}
                                onClick={() => handleConfirmClaim(item)}
                              >
                                Claim
                              </button>
                            </div>
                          ) : item.claimedBy ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {!isItemPacked(item) ? (
                                <span className="packing-carrier-tag">
                                  Packer: {getUserName(users, item.claimedBy)}
                                  <button
                                    className="btn-inline-delete-cross"
                                    style={{ padding: '0 0 0 4px', lineHeight: 1 }}
                                    onClick={() => handleUnclaim(item)}
                                    title="Unassign item carrier"
                                  >
                                    ×
                                  </button>
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  className="btn-inline-delete-cross"
                                  style={{ fontSize: '1.25rem', lineHeight: 1 }}
                                  onClick={() => handleDeleteItem(item)}
                                  title="Delete item"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <button className="btn-claim" onClick={() => handleStartClaim(item.id)}>
                                Claim Item
                              </button>
                              <button
                                type="button"
                                className="btn-inline-delete-cross"
                                style={{ fontSize: '1.25rem', lineHeight: 1 }}
                                onClick={() => handleDeleteItem(item)}
                                title="Delete item"
                              >
                                ×
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Everybody brings Section */}
              <div className="packing-category-section" style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontFamily: 'var(--font-title)',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: 'var(--color-primary)',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid var(--color-glass-border)',
                  paddingBottom: '4px'
                }}>
                  Everybody brings ({personalActiveItems.length})
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {personalActiveItems.length === 0 ? (
                    <div style={{
                      fontSize: '0.8rem',
                      color: 'var(--color-text-muted)',
                      padding: '14px 20px',
                      background: 'rgba(255, 255, 255, 0.01)',
                      border: '1px dashed var(--color-glass-border)',
                      borderRadius: '12px',
                      fontStyle: 'italic',
                      textAlign: 'center'
                    }}>
                      No items to bring.
                    </div>
                  ) : (
                    personalActiveItems.map(item => (
                      <div
                        key={item.id}
                        className={`packing-item-card ${isItemPacked(item) ? 'packed' : ''}`}
                        style={isItemPacked(item) ? { opacity: 0.6 } : undefined}
                      >
                        <div className="packing-item-left">
                          <div
                            className={`packing-checkbox ${isItemPacked(item) ? 'checked' : ''}`}
                            onClick={() => handleTogglePacked(item)}
                          />

                          <div className="packing-text-block">
                            <span className="packing-name" style={isItemPacked(item) ? { textDecoration: 'line-through' } : undefined}>
                              {item.name}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px', flexWrap: 'wrap' }}>
                              <span className="packing-metadata">
                                Added by: <span>{getUserName(users, item.addedBy)}</span>
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="packing-item-actions">
                          <button
                            type="button"
                            className="btn-inline-delete-cross"
                            style={{ fontSize: '1.25rem', lineHeight: 1 }}
                            onClick={() => handleDeleteItem(item)}
                            title="Delete item"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Packed Section */}
              {packedItems.length > 0 && (
                <div className="packing-category-section" style={{ marginTop: '24px' }}>
                  <h4 style={{
                    fontFamily: 'var(--font-title)',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: 'var(--color-text-muted)',
                    marginBottom: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid var(--color-glass-border)',
                    paddingBottom: '4px'
                  }}>
                    Packed ({packedItems.length})
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {packedItems.map(item => (
                      <div
                        key={item.id}
                        className="packing-item-card claimed packed"
                        style={{ opacity: 0.6 }}
                      >
                        <div className="packing-item-left">
                          <div
                            className="packing-checkbox checked"
                            onClick={() => handleTogglePacked(item)}
                          />
                          <div className="packing-text-block">
                            <span className="packing-name" style={{ textDecoration: 'line-through' }}>
                              {item.name}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px', flexWrap: 'wrap' }}>
                              <span className="packing-metadata">
                                Added by: <span>{getUserName(users, item.addedBy)}</span>
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="packing-item-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {item.isShared && item.claimedBy && (
                            <span className="packing-carrier-tag" style={{ background: 'rgba(148, 163, 184, 0.1)', color: 'var(--color-text-muted)', borderColor: 'transparent' }}>
                              Packed by: {getUserName(users, item.claimedBy)}
                            </span>
                          )}
                          <button
                            type="button"
                            className="btn-inline-delete-cross"
                            style={{ fontSize: '1.25rem', lineHeight: 1 }}
                            onClick={() => handleDeleteItem(item)}
                            title="Delete item"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
